import { useCallback, useMemo } from 'react'
import { STUDENT_APPLICATION_COUNTRY_OPTIONS } from '../../../constants/studentApplicationCountries'
import { resolveStudentCountryIso2List } from '../../../constants/studentCountriesDisplay'
import { studentsFiltersStore } from '../../../model/studentsFiltersStore'
import { studentsStore } from '../../../model/studentsStore'

export const useFiltersStudentsContent = ({ onClose } = {}) => {
	const st = studentsFiltersStore
	const students = studentsStore.students

	const countryCountsByIso2 = useMemo(() => {
		const counts = {}
		for (const s of students) {
			for (const code of resolveStudentCountryIso2List(s)) {
				counts[code] = (counts[code] || 0) + 1
			}
		}
		return counts
	}, [students])

	const countryOptions = useMemo(
		() =>
			STUDENT_APPLICATION_COUNTRY_OPTIONS.map(o => ({
				...o,
				count: countryCountsByIso2[o.value] ?? 0,
			})),
		[countryCountsByIso2],
	)

	const gradeOptions = useMemo(
		() => [
			{ value: 'Grade 10', label: 'Grade 10' },
			{ value: 'Grade 11', label: 'Grade 11' },
			{ value: 'Grade 12', label: 'Grade 12' },
		],
		[],
	)

	const dynamicOptions = useMemo(() => {
		const courses = new Set()

		students.forEach(student => {
			if (student.course && student.course !== 'N/A') {
				courses.add(student.course)
			}
		})

		return {
			courses: Array.from(courses)
				.sort()
				.map(c => ({
					value: c,
					label: c,
				})),
		}
	}, [students])

	const filtersConfig = useMemo(
		() => [
			{
				key: 'studentNationality',
				title: 'Country of application',
				options: countryOptions,
				selected: st.filters.studentNationality,
			},
			{
				key: 'grade',
				title: 'Grade',
				options: gradeOptions,
				selected: st.filters.grade,
			},
			{
				key: 'course',
				title: 'Course',
				options: dynamicOptions.courses,
				selected: st.filters.course,
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			st,
			countryOptions,
			gradeOptions,
			dynamicOptions,
			st.filters.studentNationality,
			st.filters.grade,
			st.filters.course,
		],
	)

	const lockedCount = st.lockedColumnIds?.length || 0

	const managerColumns = useMemo(() => {
		return (st.activeColumns || []).filter(c => !st.isLockedColumn(c.id))
	}, [st])

	const onChangeKey = useCallback(
		(key, nextSelected) => {
			st.setFilter(key, nextSelected)
		},
		[st],
	)

	const onApply = useCallback(() => {
		studentsStore.applyPanelFilters(st.filters)
		onClose?.()
	}, [st.filters, onClose])

	const onActivateColumn = useCallback(
		id => {
			st.moveToActive(id)
		},
		[st],
	)

	const onDeactivateColumn = useCallback(
		id => {
			st.moveToInactive(id)
		},
		[st],
	)

	const onReorderActive = useCallback(
		(from, to) => {
			st.reorderActive(from + lockedCount, to + lockedCount)
		},
		[st, lockedCount],
	)

	return {
		filtersConfig,
		onChangeKey,
		onApply,

		inactiveColumns: st.inactiveColumns,
		managerColumns,

		onActivateColumn,
		onDeactivateColumn,
		onReorderActive,
	}
}
